/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

// FAST TRACK: Read language instantly when the script file loads, before waiting for DOMContentLoaded
const cookieLang = document.cookie
	.split("; ")
	.find((row) => row.startsWith("siteLang="));
const initialLang = cookieLang
	? cookieLang.split("=")[1]
	: localStorage.getItem("siteLang") || "en";

// Set both parameters cleanly out of the gate
document.documentElement.setAttribute("lang", initialLang);
document.documentElement.setAttribute("data-lang", initialLang);

class Navigation {
	constructor() {
		// Selectors
		this.header = document.querySelector("#header");
		this.menuIcon = document.querySelector("#header__menu-icon");
		this.menuOverlay = document.querySelector("#menu");
		this.donateButton = document.querySelector("#header__button");
		this.body = document.body;
		this.mobileLogo = document.querySelector("#header__mobile-logo");

		// Language Switcher (add below existing selectors)
		this.langOptions = document.querySelectorAll(".lang-switcher__option");
		this.desktopCurrent = document.querySelector("#current-lang-desktop");
		this.mobileCurrent = document.querySelector("#current-lang-mobile");

		this.currentLang = initialLang;

		// Initialize language
		this.applyLanguage(this.currentLang);

		// Language Switcher Selectors
		this.desktopGlobe = document.querySelector(
			".lang-switcher--desktop .lang-switcher__icon",
		);
		this.desktopLangText = document.querySelector("#current-lang-desktop");

		// Scroll Tracking
		this.lastScrollY = 0;

		if (this.menuIcon && this.menuOverlay) {
			this.initMenuAnimation();
			this.events();
		}
	}

	initMenuAnimation() {
		this.menuTl = gsap.timeline({
			paused: true,
			defaults: { ease: "power3.inOut" },
		});

		this.menuTl
			.to(this.menuOverlay, {
				duration: 0.4,
				autoAlpha: 1,
			})
			.from(
				// Added .lang-switcher--mobile to the staggered items
				".menu__item, .menu__social, .menu__logo, .lang-switcher--mobile",
				{
					duration: 0.5,
					y: 100,
					opacity: 0,
					stagger: 0.1,
					clearProps: "transform",
				},
				"-=0.2",
			);
	}

	events() {
		this.menuIcon.addEventListener("click", () => this.toggleMenu());

		const allAnchorLinks = document.querySelectorAll('a[href*="#"]');
		allAnchorLinks.forEach((link) => {
			link.addEventListener("click", (e) => this.handleLinkClick(e));
		});

		document.addEventListener("keydown", (e) => {
			if (
				e.key === "Escape" &&
				this.menuOverlay.classList.contains("is-active")
			) {
				this.closeMenu();
			}
		});

		// Language switch events
		this.langOptions.forEach((btn) => {
			btn.addEventListener("click", (e) => {
				const selectedLang = e.currentTarget.dataset.lang;
				this.setLanguage(selectedLang);
			});
		});

		// Mobile language toggle
		const toggles = document.querySelectorAll(".lang-switcher__toggle");

		toggles.forEach((toggle) => {
			toggle.addEventListener("click", (e) => {
				e.stopPropagation();

				const parent = toggle.closest(".lang-switcher");
				parent.classList.toggle("is-open");
			});
		});

		// Close when clicking outside
		document.addEventListener("click", () => {
			document
				.querySelectorAll(".lang-switcher")
				.forEach((el) => el.classList.remove("is-open"));
		});

		document.querySelectorAll(".lang-switcher__list").forEach((list) => {
			list.addEventListener("click", (e) => {
				e.stopPropagation();
			});
		});

		window.addEventListener("scroll", () => this.handleScroll(window), {
			passive: true,
		});
	}

	handleLinkClick(e) {
		const link = e.currentTarget;
		const href = link.getAttribute("href");

		if (href.includes("#") && !link.classList.contains("js-obfuscated-email")) {
			const url = new URL(link.href, window.location.origin);
			const targetId = url.hash;
			const isHomePage =
				window.location.pathname === "/" ||
				window.location.pathname.includes("index.php");
			const isMenuOpen = this.menuOverlay.classList.contains("is-active");

			if (isHomePage && document.querySelector(targetId)) {
				e.preventDefault();
				if (isMenuOpen) {
					this.closeMenu(() => this.scrollToSection(targetId));
				} else {
					this.scrollToSection(targetId);
				}
			} else if (isMenuOpen) {
				this.closeMenu();
			}
		}
	}

	scrollToSection(targetId) {
		gsap.to(window, {
			duration: 1.2,
			scrollTo: { y: targetId, autoKill: false, offsetY: 80 },
			ease: "power3.inOut",
		});
	}

	toggleMenu() {
		const isOpening = !this.menuOverlay.classList.contains("is-active");
		isOpening ? this.openMenu() : this.closeMenu();
	}

	openMenu() {
		this.menuIcon.classList.add("is-active");
		this.menuOverlay.classList.add("is-active");
		this.header.classList.add("is-active");
		this.body.style.overflow = "hidden";
		this.header.style.backgroundColor = "#cceef3";
		document.documentElement.style.overflow = "hidden";

		if (this.donateButton) {
			this.donateButton.style.opacity = "1";
			this.donateButton.style.visibility = "visible";
		}

		if (typeof btsData !== "undefined") this.updateAssets(true);
		this.menuIcon.setAttribute("aria-expanded", "true");
		this.menuTl.play();
	}

	closeMenu(onCompleteCallback = null) {
		this.menuTl.reverse();
		this.menuTl.eventCallback("onReverseComplete", () => {
			this.menuIcon.classList.remove("is-active");
			this.menuOverlay.classList.remove("is-active");
			this.header.classList.remove("is-active");
			this.body.style.overflow = "";
			this.header.style.backgroundColor = "";
			document.documentElement.style.overflow = "";

			if (this.donateButton) {
				this.donateButton.style.opacity = "";
				this.donateButton.style.visibility = "";
			}

			if (typeof btsData !== "undefined") this.updateAssets(false);
			this.menuIcon.setAttribute("aria-expanded", "false");
			if (onCompleteCallback) onCompleteCallback();
		});
	}

	handleScroll(target) {
		const currentScrollY =
			target === window ? window.scrollY : target.scrollTop;
		const isMenuOpen = this.menuOverlay.classList.contains("is-active");

		if (currentScrollY > this.lastScrollY && currentScrollY > 50) {
			if (!isMenuOpen) this.header.classList.add("header--hidden");
		} else if (currentScrollY < this.lastScrollY) {
			this.header.classList.remove("header--hidden");
			this.header.classList.toggle(
				"header--scrolled",
				currentScrollY > 0 || isMenuOpen,
			);
		}
		this.lastScrollY = currentScrollY;
	}

	updateAssets(isActive) {
		if (this.mobileLogo) {
			this.mobileLogo.src = isActive
				? btsData.logoMobileAlt
				: btsData.logoMobile;
		}

		// --- Desktop Language Switcher Asset Swap ---
		if (
			this.desktopGlobe &&
			this.desktopLangText &&
			typeof btsData !== "undefined"
		) {
			// Icon Swap
			this.desktopGlobe.src = isActive
				? btsData.globeBlack
				: btsData.globeWhite;
			// Text Color Swap (via direct style to ensure it overrides SCSS)
			this.desktopLangText.style.color = isActive ? "#000000" : "#ffffff";
		}
	}

	setLanguage(lang) {
		this.currentLang = lang;

		// 1. Save for the Client-side (JS)
		localStorage.setItem("siteLang", lang);

		// 2. Save for the Server-side (PHP)
		// IMPORTANT: 'path=/' ensures the cookie is available site-wide
		document.cookie = `siteLang=${lang}; path=/; max-age=31536000; SameSite=Lax`;

		// 3. Update the UI
		document.documentElement.setAttribute("lang", lang);
		document.documentElement.setAttribute("data-lang", lang);

		this.applyLanguage(lang);

		// Close dropdowns
		document
			.querySelectorAll(".lang-switcher")
			.forEach((el) => el.classList.remove("is-open"));

		window.dispatchEvent(new CustomEvent("languageChange", { detail: lang }));
	}

	applyLanguage(lang) {
		// Update HTML lang attribute
		document.documentElement.setAttribute("lang", lang);

		const label = lang === "es" ? "ES" : "EN";

		// Desktop
		if (this.desktopCurrent) {
			this.desktopCurrent.textContent = label;
		}

		// Mobile
		if (this.mobileCurrent) {
			this.mobileCurrent.textContent = label;
		}
	}
}

window.addEventListener("DOMContentLoaded", () => {
	new Navigation();
});
